import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, FlaskConical, PackageOpen, Hand, CheckCircle2, ShieldCheck,
  Wallet, Recycle, SprayCan, Droplets, Wind, Shirt, Utensils,
} from "lucide-react";
import WaitlistModal from "../components/WaitlistModal";
import Seo from "../components/Seo";

const ACCENT = "#43C6F0";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};
const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className={className}>
    {children}
  </motion.div>
);

const WHY = [
  { icon: ShieldCheck, title: "Reduce decision risk", desc: "Evaluate how a product actually performs in your home before you commit to a full-size purchase." },
  { icon: Wallet, title: "Avoid wasted spend", desc: "Stop buying full-size products that end up unused. Try first, then invest only in what genuinely works for you." },
  { icon: Recycle, title: "Less waste, more intent", desc: "Sampling first means fewer half-used bottles under the sink and a more deliberate, lower-tox routine." },
];

const STEPS = [
  { n: "01", icon: PackageOpen, title: "Choose a curated sample box", desc: "Select from the curated sample boxes we assemble, each built around a specific low-tox category. The selection is done for you; there are no custom requests." },
  { n: "02", icon: Hand, title: "Try it at home", desc: "Live with the products across the routines that matter, cleaning, personal care, laundry, and more, so you can judge fit, feel, and performance in real conditions." },
  { n: "03", icon: CheckCircle2, title: "Decide with confidence", desc: "Switch to full-size only for the products that earn a place in your home. No guesswork, no pressure, no waste." },
];

const BOXES = [
  { icon: SprayCan, name: "Clean Home", desc: "Everyday surface, glass, and multi-purpose cleaners evaluated for lower-tox performance." },
  { icon: Droplets, name: "Personal Care", desc: "Skin, hair, and oral-care essentials, the daily, leave-on products that matter most." },
  { icon: Shirt, name: "Laundry & Textiles", desc: "Detergents and fabric care that clean well without unnecessary fragrance or residue." },
  { icon: Utensils, name: "Kitchen & Food Storage", desc: "Lower-tox cookware companions and food-storage alternatives for the spaces you cook and eat in." },
  { icon: Wind, name: "Air & Water", desc: "Filtration and air-quality companions for the highest-uptake exposure routes in the home." },
];

const CtaButton = ({ testid, className = "", children }) => (
  <WaitlistModal
    slug="sample-library"
    name="Sample Library"
    triggerTestid={testid}
    triggerClassName={className}
  >
    {children}
  </WaitlistModal>
);

const primaryBtn = "inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-6 py-3.5 cursor-pointer hover:bg-primary-hover transition-colors shadow-[0_0_20px_rgba(0,229,153,0.3)]";

export default function SampleLibraryPage() {
  return (
    <div data-testid="sample-library-page">
      <Seo title="Sample Library" path="/sample-library"
        description="Coming soon: try carefully curated lower-tox sample boxes before you commit. Reduce decision risk and evaluate products with engineering rigor. Join the waitlist." />

      {/* HERO */}
      <section className="relative grid-bg grain border-b border-border overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: `${ACCENT}66`, color: ACCENT }}>
              <FlaskConical className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full" style={{ color: ACCENT, borderColor: `${ACCENT}66`, backgroundColor: `${ACCENT}12` }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />Coming Soon
            </span>
          </div>
          <p className="mt-8 mono-label" style={{ color: ACCENT }}>Try · the second step in your journey</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.06]">
            Try lower-tox, <span style={{ color: ACCENT }}>before you commit.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            The Sample Library lets you experience carefully selected lower-tox products through curated sample
            boxes, so you can evaluate what actually works in your home before replacing what you currently use.
            It sits between learning the science and implementing lasting change.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <CtaButton testid="hero-waitlist" className={primaryBtn}>
              Join the Waitlist <ArrowRight className="h-4 w-4" />
            </CtaButton>
            <Link to="/greenpapers" data-testid="hero-learn-link"
              className="inline-flex items-center gap-2 border border-border text-foreground text-sm px-5 py-3.5 hover:border-primary hover:text-primary transition-colors">
              Start with Greenpapers <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-b border-border bg-surface/20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <Reveal className="max-w-2xl mb-12">
            <p className="mono-label">Why sample first</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Thoughtful evaluation, not impulse buying.</h2>
            <p className="mt-4 text-base text-foreground/60 leading-relaxed">
              Switching to lower-tox products is a series of decisions. Sampling turns each one into an informed
              choice instead of a gamble.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {WHY.map((w, i) => (
              <Reveal key={w.title} i={i}>
                <div data-testid={`why-${i}`} className="flex flex-col h-full bg-surface p-8">
                  <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: `${ACCENT}66`, color: ACCENT }}>
                    <w.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-medium">{w.title}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WILL WORK */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="how-it-works">
        <Reveal className="max-w-2xl mb-12">
          <p className="mono-label">How it will work</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Three simple steps.</h2>
          <p className="mt-4 text-base text-foreground/60 leading-relaxed">
            You'll choose from a set of curated sample boxes we assemble, each focused on a specific low-tox
            category. The boxes are curated for you; the Sample Library isn't a custom-request service.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} i={i}>
              <div data-testid={`step-${s.n}`} className="border-t-2 pt-6" style={{ borderColor: `${ACCENT}66` }}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-3xl font-light" style={{ color: ACCENT }}>{s.n}</span>
                  <span className="flex h-10 w-10 items-center justify-center border" style={{ borderColor: `${ACCENT}55`, color: ACCENT }}>
                    <s.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-medium">{s.title}</h3>
                <p className="mt-3 text-sm text-foreground/60 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CURATED BOXES */}
      <section className="border-y border-border bg-surface/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="mono-label">What the boxes will cover</p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Curated around the categories that matter.</h2>
              <p className="mt-4 text-base text-foreground/60 leading-relaxed">
                Each sample box focuses on a specific area of the home, built from the same lower-tox themes we
                analyze in the Greenpapers. Final line-ups are being finalized.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {BOXES.map((b, i) => (
              <Reveal key={b.name} i={i}>
                <div data-testid={`box-${i}`} className="group relative flex flex-col h-full bg-surface p-7 overflow-hidden">
                  <span className="absolute top-0 left-0 h-[3px] w-full opacity-60" style={{ background: ACCENT }} />
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center border" style={{ borderColor: `${ACCENT}55`, color: ACCENT }}>
                      <b.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border rounded-full text-foreground/45 border-border">Coming Soon</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-medium">{b.name}</h3>
                  <p className="mt-2 text-sm text-foreground/60 leading-relaxed flex-1">{b.desc}</p>
                </div>
              </Reveal>
            ))}
            {/* Waitlist tile */}
            <Reveal i={BOXES.length}>
              <div className="flex flex-col h-full bg-primary p-7">
                <p className="font-display text-lg font-medium leading-snug text-primary-foreground">More boxes are on the way.</p>
                <p className="mt-2 text-sm text-primary-foreground/80 leading-relaxed flex-1">Join the waitlist to be first in line when the Sample Library opens.</p>
                <CtaButton testid="boxes-waitlist" className="mt-5 inline-flex items-center gap-2 bg-base text-foreground text-sm font-medium px-5 py-3 cursor-pointer hover:bg-surface transition-colors self-start">
                  Join the Waitlist <ArrowRight className="h-4 w-4" />
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT FITS THE JOURNEY */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <Reveal className="max-w-3xl">
          <p className="mono-label">Where it fits</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">The bridge between learning and implementing.</h2>
          <p className="mt-5 text-lg md:text-xl font-display font-light leading-snug text-foreground/90">
            Learn through <Link to="/greenpapers" className="text-primary hover:underline">Greenpapers</Link>. Try
            through the Sample Library. Implement with <Link to="/products" className="text-primary hover:underline">Products</Link>,
            and optimize with <Link to="/services" className="text-primary hover:underline">Services</Link>.
          </p>
          <p className="mt-5 text-sm text-foreground/60 leading-relaxed max-w-2xl">
            The Sample Library is evaluated with the same engineering rigor as everything else at My Eco Engineer,
            structured, evidence-based, and focused on the decisions that actually matter.
          </p>
        </Reveal>
      </section>

      {/* FINAL WAITLIST CTA */}
      <section className="border-t border-border bg-surface/30">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 text-center">
          <div className="relative border border-border bg-base p-8 sm:p-12">
            <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2" style={{ borderColor: `${ACCENT}99` }} />
            <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2" style={{ borderColor: `${ACCENT}99` }} />
            <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2" style={{ borderColor: `${ACCENT}99` }} />
            <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2" style={{ borderColor: `${ACCENT}99` }} />
            <p className="mono-label" style={{ color: ACCENT }}>Coming soon</p>
            <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">Be first to try the Sample Library.</h2>
            <p className="mt-3 max-w-xl mx-auto text-sm text-foreground/60 leading-relaxed">
              Be the first to know when the Sample Library launches and new products become available.
            </p>
            <div className="mt-7 flex justify-center">
              <CtaButton testid="footer-waitlist" className={primaryBtn}>
                Join the Waitlist <ArrowRight className="h-4 w-4" />
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
